// Image utility functions for compression and format conversion

/**
 * Convert and compress image to JPG format with specified max file size
 * @param {File} file - Original image file
 * @param {number} maxSizeKB - Maximum file size in KB (default: 500KB)
 * @param {number} quality - Image quality 0-1 (default: 0.8)
 * @returns {Promise<File>} - Processed image file
 */
export const compressImageToJPG = (file, maxSizeKB = 500, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    if (!file || !file.type.startsWith('image/')) {
      reject(new Error('Invalid file type. Please select an image file.'));
      return;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Set canvas size to image size
      canvas.width = img.width;
      canvas.height = img.height;

      // Draw image on canvas
      ctx.drawImage(img, 0, 0);

      // Function to get blob with specified quality
      const getCompressedBlob = currentQuality => {
        return new Promise(blobResolve => {
          canvas.toBlob(
            blob => {
              blobResolve(blob);
            },
            'image/jpeg',
            currentQuality
          );
        });
      };

      // Binary search for optimal quality to meet size requirement
      const findOptimalQuality = async () => {
        let minQuality = 0.1;
        let maxQuality = quality;
        let currentQuality = quality;
        let resultBlob = null;

        // Initial check with provided quality
        resultBlob = await getCompressedBlob(currentQuality);

        if (resultBlob.size <= maxSizeKB * 1024) {
          // Already within size limit
          const fileName = file.name.replace(/\.[^/.]+$/, '.jpg');
          const compressedFile = new File([resultBlob], fileName, {
            type: 'image/jpeg',
            lastModified: Date.now(),
          });
          resolve(compressedFile);
          return;
        }

        // Binary search for optimal quality
        for (let i = 0; i < 10; i++) {
          // Max 10 iterations
          currentQuality = (minQuality + maxQuality) / 2;
          resultBlob = await getCompressedBlob(currentQuality);

          if (resultBlob.size <= maxSizeKB * 1024) {
            minQuality = currentQuality;
          } else {
            maxQuality = currentQuality;
          }

          if (maxQuality - minQuality < 0.01) {
            break;
          }
        }

        // Use the last good quality
        resultBlob = await getCompressedBlob(minQuality);

        // If still too large, reduce image dimensions
        if (resultBlob.size > maxSizeKB * 1024) {
          const scaleFactor = Math.sqrt((maxSizeKB * 1024) / resultBlob.size);
          canvas.width = Math.floor(img.width * scaleFactor);
          canvas.height = Math.floor(img.height * scaleFactor);

          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          resultBlob = await getCompressedBlob(minQuality);
        }

        const fileName = file.name.replace(/\.[^/.]+$/, '.jpg');
        const compressedFile = new File([resultBlob], fileName, {
          type: 'image/jpeg',
          lastModified: Date.now(),
        });

        resolve(compressedFile);
      };

      findOptimalQuality().catch(reject);
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    // Load image
    if (file.type === 'image/jpeg' || file.type === 'image/jpg') {
      const objectUrl = URL.createObjectURL(file);

      // Store original handlers
      const originalOnload = img.onload;
      const originalOnerror = img.onerror;

      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        // Call the original onload handler
        originalOnload();
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        originalOnerror();
      };

      img.src = objectUrl;
    } else {
      // For other formats, convert to data URL first
      const reader = new FileReader();
      reader.onload = e => {
        img.src = e.target.result;
      };
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
      reader.readAsDataURL(file);
    }
  });
};

/**
 * Process multiple image files - compress and convert to JPG
 * @param {FileList|Array} files - Array of image files
 * @param {number} maxSizeKB - Maximum file size in KB per image
 * @param {function} onProgress - Progress callback (current, total)
 * @returns {Promise<Array>} - Array of processed image files
 */
export const processMultipleImages = async (files, maxSizeKB = 500, onProgress = null) => {
  const processedFiles = [];
  const totalFiles = files.length;

  for (let i = 0; i < totalFiles; i++) {
    try {
      const processedFile = await compressImageToJPG(files[i], maxSizeKB);
      processedFiles.push(processedFile);

      if (onProgress) {
        onProgress(i + 1, totalFiles);
      }
    } catch (error) {
      console.error(`Failed to process image ${files[i].name}:`, error);
      // Continue with other images even if one fails
    }
  }

  return processedFiles;
};

/**
 * Create image preview URL
 * @param {File} file - Image file
 * @returns {string} - Object URL for preview
 */
export const createImagePreview = file => {
  return URL.createObjectURL(file);
};

/**
 * Cleanup image preview URL
 * @param {string} url - Object URL to cleanup
 */
export const cleanupImagePreview = url => {
  URL.revokeObjectURL(url);
};

/**
 * Validate image file
 * @param {File} file - File to validate
 * @returns {boolean} - Whether file is a valid image
 */
export const isValidImage = file => {
  return file && file.type.startsWith('image/');
};

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted file size
 */
export const formatFileSize = bytes => {
  if (bytes === 0) {
    return '0 Bytes';
  }

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};
